import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  LifeBuoy,
  MessageSquarePlus,
  ArrowLeft,
  ArrowUpRight,
  Loader2,
  Bot,
  UserCog,
  Lock,
  Clock,
  AlertTriangle,
  RefreshCw,
  Pencil,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import { extractApiErrorMessage } from "@/lib/api-errors";
import {
  type ConversationMode,
  type ConversationModeFilter,
  type SenderType,
  type ConversationMessageItem,
  type ConversationActionState,
  type ConversationActionName,
  type ConversationSendMessagePayload,
  type ConversationSendMessageErrorBody,
  CONVERSATION_ACTION_ERROR_FALLBACK,
  CONVERSATION_PAGE_SIZE,
  CONVERSATION_LIST_POLL_MS,
  CONVERSATION_DETAIL_POLL_MS,
  formatWaId,
  relativeTime,
  lastMessagePrefix,
  resolveStaffLabel,
  startsNewDay,
  getConversationErrorResponse,
  sendConversationMedia,
  validateConversationMediaFile,
  socialChannelLabel,
  socialChannelStyle,
  type ConversationChannel,
} from "@/lib/conversations";
import ConversationModeBadge from "@/components/ConversationModeBadge";
import ConversationMessageBubble from "@/components/ConversationMessageBubble";
import ConversationDaySeparator from "@/components/ConversationDaySeparator";
import ConversationComposer from "@/components/ConversationComposer";

/**
 * Inbox de soporte de PLATAFORMA (superadmin): hosteleros que escriben al
 * número de ReservaManager. Calco a propósito de `pages/Conversations.tsx`
 * (mismas 7 rutas/semánticas, ver `Documentos/PLAN_WHATSAPP_IA.md` →
 * "Inbox de soporte de plataforma"), contra `/api/admin/platform-conversations`.
 * Diferencias del contrato: sin `locationId/locationName`, sin filtro de
 * sede, sin timers de sede (no hay `Location` de plataforma), y la fila trae
 * la identificación de la EMPRESA (`matched*`) en vez de la sede. Zona
 * superadmin: sin gates de permiso de inquilino, la API ya corta con
 * `requireSuperAdmin`.
 */

// ---------------------------------------------------------------------------
// Tipos (espejo del contrato de `/api/admin/platform-conversations`)
// ---------------------------------------------------------------------------

interface PlatformConversationCustomer {
  id: string;
  firstName: string;
  lastName: string;
}

type MatchedBy = "user" | "organization" | "location" | null;

interface PlatformMatchFields {
  /** Nombre puesto a mano desde el inbox (manda sobre el cruce por teléfono). */
  manualLabel?: string | null;
  matchedOrganizationId: string | null;
  matchedOrganizationName: string | null;
  matchedBy: MatchedBy;
  matchedContactName: string | null;
  matchAmbiguous: boolean;
}

interface PlatformConversationListItem extends PlatformMatchFields {
  id: string;
  waId: string;
  channel: ConversationChannel;
  /** Instagram y Facebook: nombre del perfil de quien escribe (no hay teléfono). */
  contactName?: string | null;
  mode: ConversationMode;
  unreadCount: number;
  lastMessageAt: string | null;
  lastCustomerMessageAt: string | null;
  handoffReason: string | null;
  handoffAt: string | null;
  assignedUserId: string | null;
  assignedUserName: string | null;
  createdAt: string;
  customer: PlatformConversationCustomer | null;
  lastMessage: {
    preview: string;
    senderType: SenderType;
    createdAt: string;
  } | null;
  canReplyFreeform: boolean;
}

interface PlatformConversationDetailData extends PlatformMatchFields {
  id: string;
  waId: string;
  channel: ConversationChannel;
  contactName?: string | null;
  mode: ConversationMode;
  unreadCount: number;
  lastMessageAt: string | null;
  lastCustomerMessageAt: string | null;
  handoffReason: string | null;
  handoffAt: string | null;
  assignedUserId: string | null;
  assignedUserName: string | null;
  createdAt: string;
  customer: PlatformConversationCustomer | null;
  canReplyFreeform: boolean;
}

const BASE_URL = "/admin/platform-conversations";

// ---------------------------------------------------------------------------
// Helpers propios de este inbox (identificación de EMPRESA — no hay
// equivalente en el inbox del inquilino, no se comparte con `lib/conversations.ts`)
// ---------------------------------------------------------------------------

/** Nombre a mostrar en cabecera/lista: empresa emparejada, aviso de
 *  ambigüedad, o el número en crudo si no hay match. */
function companyDisplayName(
  item: PlatformMatchFields & {
    waId: string;
    channel: ConversationChannel;
    contactName?: string | null;
    /** Nombre puesto a mano desde el inbox: manda sobre el cruce por teléfono. */
    manualLabel?: string | null;
  },
): string {
  if (item.manualLabel) return item.manualLabel;
  if (item.matchAmbiguous) return "Número ambiguo (varias empresas)";
  if (item.matchedOrganizationName) return item.matchedOrganizationName;
  if (item.channel === "web") return item.matchedContactName ?? "Chat web";
  // Instagram y Facebook: `waId` no es un teléfono, es un id de Meta.
  const social = socialChannelLabel(item.channel);
  if (social) return item.contactName?.trim() || `Persona de ${social}`;
  return formatWaId(item.waId);
}

/** Badge de canal: el chat web y las redes sociales (WhatsApp es el caso normal). */
function ChannelBadge({ channel }: { channel: ConversationChannel }) {
  const social = socialChannelStyle(channel);
  if (social) {
    return (
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${social.chip}`}>
        {social.label}
      </span>
    );
  }
  if (channel !== "web") return null;
  return (
    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100">
      Chat web
    </span>
  );
}

/** Construye los query params del listado sin claves `undefined` (mismo
 *  criterio que `Customers.tsx` / `Conversations.tsx`). */
function buildListParams(
  mode: ConversationModeFilter,
  page: number,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page,
    limit: CONVERSATION_PAGE_SIZE,
  };
  if (mode) params.mode = mode;
  return params;
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

/**
 * Modal "Nueva conversación" del soporte de plataforma (12-sep-2026): busca
 * un USUARIO registrado (hostelero) por nombre/email y le envía la plantilla
 * de apertura desde el número de ReservaManager
 * (POST /admin/platform-conversations/start).
 */
function StartPlatformConversationModal({
  onStarted,
  onClose,
}: {
  onStarted: (conversationId: string) => void;
  onClose: () => void;
}) {
  const { showToast } = useGlobalToast();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<
    Array<{
      id: string;
      firstName: string;
      lastName: string | null;
      email: string;
      phone: string | null;
      organization: { name: string } | null;
    }>
  >([]);
  const [searching, setSearching] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);

  useEffect(() => {
    const term = search.trim();
    let active = true;
    setSearching(true);
    // Sin término: listado inicial (no hace falta saberse los nombres de
    // memoria — petición de Luis, 12-sep-2026). Con término: filtro.
    const params: Record<string, string | number> = { limit: 15, page: 1 };
    if (term.length >= 2) params.search = term;
    const timer = setTimeout(() => {
      apiClient
        .get("/admin/users", { params })
        .then((res) => {
          if (!active) return;
          setResults(res.data.data ?? []);
        })
        .catch(() => {
          if (active) setResults([]);
        })
        .finally(() => {
          if (active) setSearching(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search]);

  const handleStart = async (userId: string) => {
    if (sendingId) return;
    setSendingId(userId);
    try {
      const res = await apiClient.post("/admin/platform-conversations/start", {
        userId,
      });
      showToast(res.data?.message || "Mensaje de inicio enviado", "success");
      const conversationId = res.data?.data?.conversationId as
        | string
        | undefined;
      if (conversationId) onStarted(conversationId);
      onClose();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo iniciar la conversación"),
        "error",
      );
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Nueva conversación de soporte
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
          >
            ✕
          </button>
        </div>
        <div className="p-4 space-y-3">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Busca un hostelero por nombre o email..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
            {searching ? (
              <div className="py-6 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              </div>
            ) : results.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400">
                Sin resultados.
              </p>
            ) : (
              results.map((u) => {
                const name = `${u.firstName} ${u.lastName ?? ""}`.trim();
                const canSend = !!u.phone;
                return (
                  <div
                    key={u.id}
                    className="py-2.5 flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {name || u.email}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {u.organization?.name ?? "—"} ·{" "}
                        {u.phone || "Sin teléfono personal en su perfil"}
                      </p>
                    </div>
                    <button
                      onClick={() => handleStart(u.id)}
                      disabled={!canSend || sendingId !== null}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex-shrink-0"
                    >
                      {sendingId === u.id ? "Enviando..." : "Iniciar chat"}
                    </button>
                  </div>
                );
              })
            )}
          </div>
          <p className="text-xs text-gray-400">
            El mensaje sale desde el número de ReservaManager y la conversación
            queda asignada a ti.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function PlatformSupport() {
  const { user: authUser } = useAuth();
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  const [startOpen, setStartOpen] = useState(false);

  // Filtros + paginación de la lista
  const [modeFilter, setModeFilter] = useState<ConversationModeFilter>("");
  const [page, setPage] = useState(1);
  const [listReloadToken, setListReloadToken] = useState(0);

  // Lista
  const [conversations, setConversations] = useState<
    PlatformConversationListItem[]
  >([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [matchDegraded, setMatchDegraded] = useState(false);

  // Selección + detalle
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Deep-link ?open=<conversationId> (emails/avisos de handoff): se consume
  // UNA vez y se limpia de la URL (mismo patrón que Conversations.tsx).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const open = searchParams.get("open");
    if (open) {
      setSelectedId(open);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [detail, setDetail] = useState<PlatformConversationDetailData | null>(
    null,
  );
  const [messages, setMessages] = useState<ConversationMessageItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Caja de respuesta
  const [replyText, setReplyText] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  // Nombre a mano de la conversación (quien escribe desde un número que no
  // está en ninguna ficha solo se ve como número).
  const [editandoNombre, setEditandoNombre] = useState(false);
  const [nombreBorrador, setNombreBorrador] = useState("");
  const [guardandoNombre, setGuardandoNombre] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleModeFilterChange = (value: string) => {
    setModeFilter(value as ConversationModeFilter);
    setPage(1);
  };

  const handleClearFilters = () => {
    setModeFilter("");
    setPage(1);
  };

  const handleSelectConversation = (id: string) => {
    setSelectedId(id);
    setReplyText("");
    setAttachment(null);
    setDetailError(null);
  };

  // ── Lista: fetch + polling (30s), mismo patrón anti-carreras que
  // `Conversations.tsx` (bandera `active` + AbortController propio por
  // petición, ver Dashboard.tsx).
  useEffect(() => {
    let active = true;
    let currentController: AbortController | null = null;

    const load = async (opts?: { silent?: boolean }) => {
      currentController?.abort();
      const controller = new AbortController();
      currentController = controller;
      if (!opts?.silent) setListLoading(true);
      try {
        const response = await apiClient.get<{
          success: true;
          data: PlatformConversationListItem[];
          pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
          };
          meta: { matchDegraded: boolean };
        }>(BASE_URL, {
          params: buildListParams(modeFilter, page),
          signal: controller.signal,
        });
        if (!active) return;
        setConversations(response.data.data);
        setTotal(response.data.pagination.total);
        setTotalPages(response.data.pagination.totalPages);
        setMatchDegraded(!!response.data.meta?.matchDegraded);
        setListError(null);
      } catch (err) {
        if (!active || controller.signal.aborted) return;
        setListError(
          extractApiErrorMessage(
            err,
            "No se pudieron cargar las conversaciones",
          ),
        );
      } finally {
        if (active) setListLoading(false);
      }
    };

    load();
    const interval = setInterval(
      () => load({ silent: true }),
      CONVERSATION_LIST_POLL_MS,
    );

    return () => {
      active = false;
      currentController?.abort();
      clearInterval(interval);
    };
  }, [modeFilter, page, listReloadToken]);

  // ── Detalle: fetch + polling (15s) de la conversación seleccionada.
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setMessages([]);
      setDetailError(null);
      return;
    }

    let active = true;
    let currentController: AbortController | null = null;

    const load = async (opts?: { silent?: boolean }) => {
      currentController?.abort();
      const controller = new AbortController();
      currentController = controller;
      if (!opts?.silent) setDetailLoading(true);
      try {
        const response = await apiClient.get<{
          success: true;
          data: {
            conversation: Omit<
              PlatformConversationDetailData,
              "canReplyFreeform"
            >;
            messages: ConversationMessageItem[];
            canReplyFreeform: boolean;
          };
          meta: { matchDegraded: boolean };
        }>(`${BASE_URL}/${selectedId}`, {
          signal: controller.signal,
        });
        if (!active) return;
        const { conversation, messages: msgs, canReplyFreeform } =
          response.data.data;
        setDetail({ ...conversation, canReplyFreeform });
        setMessages(msgs);
        setMatchDegraded((prev) => prev || !!response.data.meta?.matchDegraded);
        setDetailError(null);
        // El servidor ya puso unreadCount a 0 al abrir el detalle; se refleja
        // también en la lista sin esperar al siguiente poll.
        setConversations((prev) =>
          prev.map((c) =>
            c.id === selectedId ? { ...c, unreadCount: 0 } : c,
          ),
        );
      } catch (err) {
        if (!active || controller.signal.aborted) return;
        setDetailError(
          extractApiErrorMessage(err, "No se pudo cargar la conversación"),
        );
      } finally {
        if (active) setDetailLoading(false);
      }
    };

    load();
    const interval = setInterval(
      () => load({ silent: true }),
      CONVERSATION_DETAIL_POLL_MS,
    );

    return () => {
      active = false;
      currentController?.abort();
      clearInterval(interval);
    };
  }, [selectedId]);

  // Scroll al fondo al abrir una conversación o al llegar mensajes nuevos.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedId, messages.length]);

  // ── Aplica el estado devuelto por take-over / return-to-bot / close a la
  // lista y al detalle a la vez, sin esperar al siguiente poll.
  const applyActionState = useCallback(
    (state: ConversationActionState) => {
      setDetail((prev) => {
        if (!prev || prev.id !== state.id) return prev;
        const assignedUserName =
          state.assignedUserId === null
            ? null
            : state.assignedUserId === authUser?.id
              ? `${authUser?.firstName ?? ""} ${authUser?.lastName ?? ""}`.trim() ||
                prev.assignedUserName
              : prev.assignedUserName;
        return {
          ...prev,
          mode: state.mode,
          assignedUserId: state.assignedUserId,
          assignedUserName,
          handoffReason: state.handoffReason,
          handoffAt: state.handoffAt,
          unreadCount: state.unreadCount,
          lastMessageAt: state.lastMessageAt,
          lastCustomerMessageAt: state.lastCustomerMessageAt,
          canReplyFreeform: state.canReplyFreeform,
        };
      });
      setConversations((prev) =>
        prev.map((c) =>
          c.id === state.id
            ? {
                ...c,
                mode: state.mode,
                assignedUserId: state.assignedUserId,
                handoffReason: state.handoffReason,
                handoffAt: state.handoffAt,
                unreadCount: state.unreadCount,
                lastMessageAt: state.lastMessageAt,
                lastCustomerMessageAt: state.lastCustomerMessageAt,
                canReplyFreeform: state.canReplyFreeform,
              }
            : c,
        ),
      );
    },
    [authUser],
  );

  /** Guarda (o quita) el nombre manual y lo refleja en lista y detalle. */
  const guardarNombre = useCallback(async () => {
    if (!selectedId) return;
    setGuardandoNombre(true);
    try {
      const label = nombreBorrador.trim();
      await apiClient.patch(
        `/admin/platform-conversations/${selectedId}/label`,
        { label },
      );
      const manualLabel = label || null;
      setDetail((prev) =>
        prev && prev.id === selectedId ? { ...prev, manualLabel } : prev,
      );
      setConversations((prev) =>
        prev.map((c) => (c.id === selectedId ? { ...c, manualLabel } : c)),
      );
      setEditandoNombre(false);
    } catch {
      /* el siguiente poll trae el estado real */
    } finally {
      setGuardandoNombre(false);
    }
  }, [selectedId, nombreBorrador]);

  const runAction = useCallback(
    async (action: ConversationActionName, confirmFirst?: () => Promise<boolean>) => {
      if (!selectedId || actionLoading) return;
      if (confirmFirst) {
        const ok = await confirmFirst();
        if (!ok) return;
      }
      setActionLoading(true);
      try {
        const response = await apiClient.post<{
          success: true;
          message: string;
          data: ConversationActionState;
        }>(`${BASE_URL}/${selectedId}/${action}`);
        applyActionState(response.data.data);
        showToast(response.data.message, "success");
      } catch (err) {
        showToast(
          extractApiErrorMessage(err, CONVERSATION_ACTION_ERROR_FALLBACK[action]),
          "error",
        );
      } finally {
        setActionLoading(false);
      }
    },
    [selectedId, actionLoading, applyActionState, showToast],
  );

  // Reabrir la ventana de 24h: envía la plantilla de apertura al hostelero
  // desde el número de ReservaManager (mismo endpoint que el modal, variante
  // por conversación). La escritura libre vuelve cuando él responda.
  const [reopening, setReopening] = useState(false);
  const handleReopenWindow = useCallback(async () => {
    if (!selectedId || reopening) return;
    setReopening(true);
    try {
      const res = await apiClient.post("/admin/platform-conversations/start", {
        conversationId: selectedId,
      });
      showToast(res.data?.message || "Mensaje de inicio enviado", "success");
      setListReloadToken((t) => t + 1);
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo enviar el mensaje de inicio"),
        "error",
      );
    } finally {
      setReopening(false);
    }
  }, [selectedId, reopening, showToast]);

  const handleTakeOver = useCallback(() => runAction("take-over"), [runAction]);
  const handleReturnToBot = useCallback(
    () => runAction("return-to-bot"),
    [runAction],
  );
  const handleClose = useCallback(
    () =>
      runAction("close", () =>
        confirm({
          title: "Cerrar conversación",
          message:
            "El hostelero dejará de aparecer en el inbox de soporte. Si vuelve a escribir por WhatsApp, la conversación se reabre automáticamente en modo bot.",
          confirmText: "Cerrar conversación",
          cancelText: "Cancelar",
          danger: true,
        }),
      ),
    [runAction, confirm],
  );

  // ── Enviar respuesta. Sin optimismo: se añade el mensaje que devuelve el
  // servidor. En 502 (Meta rechazó el envío) el mensaje SÍ quedó guardado —
  // se añade igual, marcado como no entregado.
  const appendSentMessage = useCallback(
    (payload: ConversationSendMessagePayload, notDelivered: boolean) => {
      setMessages((prev) => [
        ...prev,
        notDelivered ? { ...payload.message, notDelivered: true } : payload.message,
      ]);
      setDetail((prev) => {
        if (!prev || prev.id !== payload.conversation.id) return prev;
        const assignedUserName =
          payload.conversation.assignedUserId === authUser?.id
            ? `${authUser?.firstName ?? ""} ${authUser?.lastName ?? ""}`.trim() ||
              prev.assignedUserName
            : prev.assignedUserName;
        return {
          ...prev,
          mode: payload.conversation.mode,
          assignedUserId: payload.conversation.assignedUserId,
          assignedUserName,
          lastMessageAt: payload.message.createdAt,
        };
      });
      setConversations((prev) =>
        prev.map((c) =>
          c.id === payload.conversation.id
            ? {
                ...c,
                mode: payload.conversation.mode,
                assignedUserId: payload.conversation.assignedUserId,
                unreadCount: 0,
                lastMessageAt: payload.message.createdAt,
                lastMessage: {
                  preview: payload.message.body.slice(0, 80),
                  senderType: payload.message.senderType,
                  createdAt: payload.message.createdAt,
                },
              }
            : c,
        ),
      );
    },
    [authUser],
  );

  const handleSendMessage = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const text = replyText.trim();
      if ((!text && !attachment) || !selectedId || sending) return;
      if (attachment && text.length > 1024) {
        showToast(
          "El texto que acompaña al archivo no puede superar 1024 caracteres.",
          "error",
        );
        return;
      }
      setSending(true);
      try {
        const response = attachment
          ? await sendConversationMedia(`${BASE_URL}/${selectedId}`, attachment, text)
          : await apiClient.post<{
              success: true;
              message: string;
              data: ConversationSendMessagePayload;
            }>(`${BASE_URL}/${selectedId}/messages`, { body: text });
        appendSentMessage(response.data.data, false);
        setReplyText("");
        setAttachment(null);
      } catch (err) {
        const errResponse = getConversationErrorResponse(err);
        const body = errResponse?.data as
          | ConversationSendMessageErrorBody
          | undefined;
        if (errResponse?.status === 502 && body?.data) {
          appendSentMessage(body.data, true);
          setReplyText("");
          setAttachment(null);
          showToast(
            body.message || "WhatsApp no ha aceptado el mensaje.",
            "error",
          );
        } else {
          showToast(
            extractApiErrorMessage(err, "No se pudo enviar el mensaje"),
            "error",
          );
        }
      } finally {
        setSending(false);
      }
    },
    [replyText, attachment, selectedId, sending, appendSentMessage, showToast],
  );

  /** Clip del composer: valida formato y tamaño antes de dejarlo adjunto. */
  const handleAttachmentChange = useCallback(
    (file: File | null) => {
      const error = file ? validateConversationMediaFile(file) : null;
      if (error) {
        showToast(error, "error");
        return;
      }
      setAttachment(file);
    },
    [showToast],
  );

  const noFiltersActive = modeFilter === "";
  // Mismo patrón de altura que `Conversations.tsx` (lección 18-ago-2026):
  // `100dvh` en móvil (barra de URL dinámica de iOS Safari), `flex-1 min-h-0`
  // en toda la cadena para que el scroll interno dispare de verdad.
  const listColumnClasses = `${
    selectedId ? "hidden md:flex" : "flex"
  } w-full md:w-80 lg:w-96 flex-1 md:flex-none min-h-0 flex-col border-r border-gray-200 bg-white overflow-hidden`;
  const chatColumnClasses = `${
    selectedId ? "flex" : "hidden md:flex"
  } flex-1 min-h-0 flex-col overflow-hidden bg-gray-50`;

  return (
    <>
      {startOpen && (
        <StartPlatformConversationModal
          onStarted={(conversationId) => {
            setSelectedId(conversationId);
            setListReloadToken((t) => t + 1);
          }}
          onClose={() => setStartOpen(false)}
        />
      )}
    <div className="flex-1 min-h-0 flex flex-col md:flex-row bg-gray-50 overflow-hidden -m-4 lg:-m-8">
      {/* ── Lista ── */}
      <div className={listColumnClasses}>
        <div className="flex-shrink-0 border-b border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <LifeBuoy className="w-5 h-5 text-primary-600 flex-shrink-0" />
            <h1 className="text-lg font-bold text-gray-900 flex-1">
              Soporte
            </h1>
            <button
              onClick={() => setStartOpen(true)}
              className="p-1.5 rounded-lg text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 flex-shrink-0 transition-colors"
              title="Nueva conversación: escribir a un hostelero"
            >
              <MessageSquarePlus className="w-4 h-4" />
            </button>
          </div>
          <select
            value={modeFilter}
            onChange={(e) => handleModeFilterChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          >
            <option value="">Todas</option>
            <option value="bot">Bot</option>
            <option value="human">Atención humana</option>
            <option value="closed">Cerradas</option>
          </select>
          {matchDegraded && (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-2">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              La identificación de empresas puede estar incompleta ahora
              mismo. "Sin identificar" no significa que no sea cliente.
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {listLoading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : listError ? (
            <div className="p-6 text-center">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600 mb-3">{listError}</p>
              <button
                onClick={() => setListReloadToken((t) => t + 1)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:underline"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Reintentar
              </button>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <LifeBuoy className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              {noFiltersActive ? (
                <>
                  <p className="font-medium text-gray-700">
                    Todavía no hay conversaciones de soporte
                  </p>
                  <p className="text-sm mt-1">
                    Aquí verás los mensajes de WhatsApp de los hosteleros al
                    número de ReservaManager, con la opción de tomar el
                    control cuando haga falta.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm">No hay conversaciones con este filtro.</p>
                  <button
                    onClick={handleClearFilters}
                    className="mt-2 text-sm font-medium text-primary-600 hover:underline"
                  >
                    Quitar filtros
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {conversations.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelectConversation(item.id)}
                  className={`w-full text-left p-3 md:p-4 flex items-start gap-2 min-w-0 hover:bg-gray-50 transition-colors ${
                    selectedId === item.id ? "bg-primary-50" : ""
                  } ${socialChannelStyle(item.channel)?.stripe ?? ""}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-gray-900 truncate">
                        {item.matchAmbiguous && "⚠️ "}
                        {companyDisplayName(item)}
                      </p>
                      <span className="flex-shrink-0 text-xs text-gray-400">
                        {relativeTime(item.lastMessageAt ?? item.createdAt)}
                      </span>
                    </div>
                    {item.matchedOrganizationName && item.matchedContactName && (
                      <p className="text-xs text-gray-500 truncate">
                        {item.matchedContactName}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <ConversationModeBadge mode={item.mode} />
                      <ChannelBadge channel={item.channel} />
                      {/* Si es ambiguo, `companyDisplayName` oculta el
                          número a propósito (ver contrato) — se muestra aquí
                          para no dejar la fila sin ningún dato identificable.
                          Si no hay match, el número YA es el título de
                          arriba: no repetir. */}
                      {item.matchAmbiguous && (
                        <span className="text-xs text-gray-400 truncate">
                          {formatWaId(item.waId)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate mt-1">
                      {item.lastMessage
                        ? `${lastMessagePrefix(item.lastMessage.senderType)}${item.lastMessage.preview}`
                        : "Sin mensajes todavía"}
                    </p>
                  </div>
                  {item.unreadCount > 0 && (
                    <span className="flex-shrink-0 mt-0.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center">
                      {item.unreadCount > 99 ? "99+" : item.unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex-shrink-0 flex items-center justify-between gap-2 border-t border-gray-200 px-3 py-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="text-xs text-gray-500">
              Página {page} de {totalPages} · {total}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* ── Chat ── */}
      <div className={chatColumnClasses}>
        {!selectedId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 p-8">
            <LifeBuoy className="w-12 h-12 mb-3" />
            <p>Selecciona una conversación para ver los mensajes.</p>
          </div>
        ) : detailLoading && !detail ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : detailError && !detail ? (
          <div className="flex-1 flex items-center justify-center p-6 text-center">
            <div>
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600">{detailError}</p>
            </div>
          </div>
        ) : detail ? (
          <>
            {/* Cabecera */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3">
              <div className="flex items-start gap-2">
                <button
                  className="md:hidden p-1.5 -ml-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0"
                  onClick={() => setSelectedId(null)}
                  title="Volver a la lista"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold text-gray-900 truncate">
                      {detail.matchAmbiguous && "⚠️ "}
                      {companyDisplayName(detail)}
                    </h2>
                    <ConversationModeBadge mode={detail.mode} />
                    <ChannelBadge channel={detail.channel} />
                    <button
                      onClick={() => {
                        setNombreBorrador(detail.manualLabel ?? "");
                        setEditandoNombre(true);
                      }}
                      title="Poner un nombre a esta conversación"
                      className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-500 flex-wrap">
                    <span>
                      {detail.channel === "web"
                        ? "Chat integrado del dashboard"
                        : socialChannelLabel(detail.channel)
                          ? `Mensaje directo de ${socialChannelLabel(detail.channel)}`
                          : formatWaId(detail.waId)}
                    </span>
                    {detail.matchedOrganizationName && detail.matchedContactName && (
                      <>
                        <span>·</span>
                        <span className="truncate">
                          {detail.matchedContactName}
                        </span>
                      </>
                    )}
                    {detail.assignedUserName && (
                      <>
                        <span>·</span>
                        <span className="truncate">
                          Asignada a {detail.assignedUserName}
                        </span>
                      </>
                    )}
                  </div>
                  {detail.matchedOrganizationId && (
                    <Link
                      to={`/admin/organizations/${detail.matchedOrganizationId}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline mt-1"
                    >
                      Ver organización <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {(detail.mode === "bot" || detail.mode === "closed") && (
                  <button
                    onClick={handleTakeOver}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 disabled:opacity-50 transition-colors"
                  >
                    <UserCog className="w-3.5 h-3.5" /> Tomar el control
                  </button>
                )}
                {detail.mode === "human" && (
                  <button
                    onClick={handleReturnToBot}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                  >
                    <Bot className="w-3.5 h-3.5" /> Devolver al bot
                  </button>
                )}
                {detail.mode !== "closed" && (
                  <button
                    onClick={handleClose}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 transition-colors"
                  >
                    <Lock className="w-3.5 h-3.5" /> Cerrar
                  </button>
                )}
              </div>
            </div>

            {/* Mensajes */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <p className="text-center text-sm text-gray-400 mt-8">
                  Todavía no hay mensajes en esta conversación.
                </p>
              ) : (
                messages.map((msg, index) => (
                  <Fragment key={msg.id}>
                    {startsNewDay(msg.createdAt, messages[index - 1]?.createdAt) && (
                      <ConversationDaySeparator createdAt={msg.createdAt} />
                    )}
                    <ConversationMessageBubble
                      message={msg}
                      mediaBasePath={`/admin/platform-conversations/${detail.id}/media`}
                      customerLabel={companyDisplayName(detail)}
                      staffLabel={(m) =>
                        resolveStaffLabel(m, detail, authUser?.id)
                      }
                    />
                  </Fragment>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Caja de respuesta */}
            {detail.mode === "closed" ? (
              <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 p-3">
                <textarea
                  disabled
                  rows={2}
                  placeholder="Esta conversación está cerrada"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-100 text-gray-400 resize-none cursor-not-allowed"
                />
                <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
                  <p className="text-sm text-gray-500">
                    Si el hostelero vuelve a escribir, se reabre sola en modo
                    bot.
                  </p>
                  <button
                    onClick={handleTakeOver}
                    disabled={actionLoading}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors flex-shrink-0"
                  >
                    Reabrir (tomar el control)
                  </button>
                </div>
              </div>
            ) : !detail.canReplyFreeform ? (
              <div className="flex-shrink-0 bg-amber-50 border-t border-amber-200 p-3">
                <textarea
                  disabled
                  rows={2}
                  placeholder={`Fuera de la ventana de 24h de ${socialChannelLabel(detail.channel) ?? "WhatsApp"}`}
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-amber-100/60 text-amber-700 placeholder-amber-500 resize-none cursor-not-allowed"
                />
                <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
                  <p className="flex items-center gap-2 text-sm text-amber-700">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    {socialChannelLabel(detail.channel)
                      ? `Han pasado más de 24h desde su último mensaje. En ${socialChannelLabel(detail.channel)} solo se puede contestar dentro de ese plazo: hay que esperar a que vuelva a escribir.`
                      : "Han pasado más de 24h desde su último mensaje. Envíale el mensaje de inicio para retomar el contacto."}
                  </p>
                  {/* En Instagram y Facebook no hay mensaje de inicio que enviar. */}
                  {!socialChannelLabel(detail.channel) && (
                    <button
                      onClick={handleReopenWindow}
                      disabled={reopening}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors flex-shrink-0"
                    >
                      {reopening ? "Enviando..." : "Enviar mensaje de inicio"}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <ConversationComposer
                value={replyText}
                onChange={setReplyText}
                onSubmit={handleSendMessage}
                sending={sending}
                attachment={attachment}
                // El chat web no pasa por WhatsApp, y por Instagram y Facebook
                // desde aquí solo sale texto: ahí no hay fotos ni vídeos.
                onAttachmentChange={
                  detail.channel === "web" || socialChannelLabel(detail.channel)
                    ? undefined
                    : handleAttachmentChange
                }
              />
            )}
          </>
        ) : null}
      </div>
    </div>

    {editandoNombre && selectedId && (
      <div
        // En el móvil el teclado tapa la mitad de abajo: la ventana se
          // pega arriba para que el campo quede siempre a la vista.
          className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-16 sm:items-center sm:pt-4"
        onClick={() => setEditandoNombre(false)}
      >
        <div
          className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-base font-semibold text-gray-900">
            Nombre de esta conversación
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Se usa en tu bandeja para reconocer quién escribe. Déjalo vacío
            para volver al nombre automático por teléfono.
          </p>
          <input
            autoFocus
            value={nombreBorrador}
            onChange={(e) => setNombreBorrador(e.target.value.slice(0, 80))}
            onKeyDown={(e) => {
              if (e.key === "Enter") void guardarNombre();
              if (e.key === "Escape") setEditandoNombre(false);
            }}
            placeholder="María (Centro Elle)"
            className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => setEditandoNombre(false)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={() => void guardarNombre()}
              disabled={guardandoNombre}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {guardandoNombre ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
